'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '../ui/Button';
import { Search, Phone, MapPin, Clock, ChevronDown, Menu, X, Tag, Home, Info, ShoppingBag, Image as ImageIcon, Star, MessageCircle, BookOpen, Palette } from 'lucide-react';
// import { useTranslation } from 'react-i18next';
import { usePathname } from 'next/navigation';
import SearchModal from '../ui/SearchModal';
import { BRAND_KNOWN_AS, BRAND_LEGAL_NAME } from '@/lib/brand';

const Header = () => {
  // const { t } = useTranslation(['common', 'header']);
  const [isSearchModalOpen, setSearchModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isTop, setIsTop] = useState(true);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const pathname = usePathname();

  // Gezinme öğeleri
  const navItems = [
    { label: 'Anasayfa', href: '/', icon: Home },
    { label: 'Hakkımızda', href: '/hakkimizda', icon: Info },
    { 
      label: 'Ürünlerimiz', 
      href: '/urunler',
      icon: ShoppingBag,
      subItems: [
        { label: 'Tümü', href: '/urunler' },
        { label: 'Berber Koltukları', href: '/urunler/kategori/berber-koltuklari' },
        { label: 'Berber Tezgahları', href: '/urunler/kategori/berber-tezgahlari' },
        { label: 'Kuaför Tezgahları', href: '/urunler/kategori/kuafor-tezgahlari' },
        { label: 'Makyaj Koltukları', href: '/urunler/kategori/makyaj-koltuklari' },
        { label: 'Terapi Yatakları', href: '/urunler/kategori/terapi-yataklari' },
        { label: 'Tarama Koltukları', href: '/urunler/kategori/tarama-koltuklari' },
        { label: 'Çocuk Koltukları', href: '/urunler/kategori/cocuk-koltuklari' },
        { label: 'Çocuk Tezgahları', href: '/urunler/kategori/cocuk-tezgahlari' },
        { label: 'Bekleme Koltukları', href: '/urunler/kategori/bekleme-koltuklari' },
        { label: 'Yıkama Setleri', href: '/urunler/kategori/yikama-setleri' },
        { label: 'Yardımcı Ekipmanlar', href: '/urunler/kategori/yardimci-ekipmanlar' },
        { label: 'Laboratuvar Dolapları', href: '/urunler/kategori/laboratuvar-dolaplari' },
        { label: 'Spa Ürünleri', href: '/urunler/kategori/spa-urunleri' },
        { label: 'Ağda Salon Ürünleri', href: '/urunler/kategori/agda-salon-urunleri' },
        { label: 'Havluluk ve Dolaplar', href: '/urunler/kategori/havluluk-ve-dolaplar' },
        { label: 'Banko & Kasiyer', href: '/urunler/kategori/banko-kasiyer' },
        { label: 'Yedek Parçalar', href: '/urunler/kategori/yedek-parcalar' },
        { label: 'Lavabo Modelleri', href: '/urunler/kategori/lavabo-modelleri' }
      ]
    },
    { label: 'Galeri', href: '/galeri', icon: ImageIcon },
    { label: 'Referanslar', href: '/referanslar', icon: Star },
    { label: 'Blog', href: '/blog', icon: BookOpen },
    { label: 'İletişim', href: '/iletisim', icon: MessageCircle },
  ];

  // Sayfa kaydırıldığında başlık stilini değiştir
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      
      // İlk 20px içindeyse en üstte sayılır
      if (scrollPosition < 20) {
        setIsTop(true);
        setScrolled(false);
      } else if (scrollPosition > 50) {
        setIsTop(false);
        setScrolled(true);
      } else {
        setIsTop(false);
        setScrolled(false);
      }
    };

    // İlk yüklemede kontrol et
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <header className={`fixed top-0 left-0 right-0 w-full max-w-none z-50 transition-transform duration-300 ${
      mobileMenuOpen ? 'h-screen md:h-auto' : ''
    }`}>
      {/* İletişim Çubuğu - Sadece en üstteyken görünür */}
      <div className={`bg-[#3F0D12] text-white transition-all duration-500 ${
        isTop && !mobileMenuOpen ? 'max-h-12 opacity-100 visible' : 'max-h-0 opacity-0 invisible overflow-hidden'
      }`}>
        <div className="container mx-auto px-4 py-2">
          <div className="flex justify-between items-center h-8">
              <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center">
                <Phone className="h-3 w-3 mr-2 text-white" aria-hidden />
                <Link href="tel:+905323457227" className="text-white hover:underline underline-offset-2 min-h-[44px] inline-flex items-center">
                  +90 532 345 72 27
                </Link>
              </div>
              <div className="hidden md:flex items-center">
                <MapPin className="h-3 w-3 mr-2 text-white shrink-0" aria-hidden />
                <span className="text-white">İstanbul, Türkiye</span>
              </div>
              <div className="hidden lg:flex items-center">
                <Clock className="h-3 w-3 mr-2 text-white shrink-0" aria-hidden />
                <span className="text-white">Pzt-Cmt: 09:00 - 18:00</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              <Link
                href="/katalog"
                target="_blank"
                rel="noopener noreferrer"
                className="header-doc-link header-doc-pulse flex min-h-[44px] items-center rounded-full px-2 py-1 text-sm text-white no-underline hover:text-white"
              >
                <BookOpen className="mr-1.5 h-3 w-3 shrink-0 text-white" aria-hidden />
                Katalog
              </Link>
              <span className="select-none text-white/50" aria-hidden>
                |
              </span>
              <Link
                href="/kartela"
                target="_blank"
                rel="noopener noreferrer"
                className="header-doc-link header-doc-pulse header-doc-pulse-delay flex min-h-[44px] items-center rounded-full px-2 py-1 text-sm text-white no-underline hover:text-white"
              >
                <Palette className="mr-1.5 h-3 w-3 shrink-0 text-white" aria-hidden />
                Renk Kartelası
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* Ana Navigasyon Bar */}
      <div className={`bg-white transition-shadow duration-300 ${
        scrolled && !mobileMenuOpen ? 'shadow-lg py-2 shadow-black/5' : 'py-2 md:py-3 md:py-4'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link href="/" className="flex items-center group">
              <div className={`relative transition-all duration-300 ${
                scrolled ? 'w-24 sm:w-28 h-9' : 'w-28 sm:w-32 h-10'
              }`}>
                <Image 
                  src="/images/logo123.webp" 
                  alt={`${BRAND_LEGAL_NAME} logosu`}
                  fill
                  className="object-contain group-hover:scale-105 transition-transform"
                  priority
                />
              </div>
              <div className={`ml-3 transition-all duration-300 ${
                scrolled ? 'scale-95 origin-left' : ''
              }`}>
                <div className="flex items-center space-x-2">
                  <div className="w-0.5 h-8 bg-gradient-to-b from-primary via-primary/80 to-primary/50 rounded hidden sm:block"></div>
                  <div>
                    <div className="flex items-center">
                      <h2 className="font-extrabold text-sm md:text-lg leading-tight tracking-tight">
                        <span className="text-primary group-hover:text-primary-dark transition-colors">Filiz</span>{' '}
                        <span className="text-gray-800 group-hover:text-gray-900 transition-colors">Dekorasyon Kozmetik</span>
                      </h2>
                    </div>
                    <p className="text-[8px] md:text-[9px] text-gray-500 font-medium tracking-wide hidden sm:block mt-0.5 pl-0.5">
                      BAY & BAYAN KUAFÖR MALZEMELERİ
                    </p>
                  </div>
                </div>
              </div>
            </Link>
            
            {/* Masaüstü Navigasyon */}
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <div key={item.label} className="relative group">
                  <Link 
                    href={item.href}
                    className={`px-3 py-2 min-h-[44px] text-sm font-medium rounded-md inline-flex items-center transition-all ${
                      pathname === item.href || pathname?.startsWith(item.href + '/') 
                        ? 'text-primary-darkest bg-red-50' 
                        : 'text-gray-800 hover:text-primary hover:bg-gray-50'
                    } ${scrolled ? 'text-[13px]' : 'text-[14px]'}`}
                  >
                    {item.icon && (
                      <item.icon className={`mr-1 transition-all duration-300 ${
                        scrolled ? 'h-3.5 w-3.5' : 'h-4 w-4'
                      }`} />
                    )}
                    {item.label}
                    {item.subItems && (
                      <ChevronDown className={`ml-1 transition-all duration-300 ${
                        scrolled ? 'h-3.5 w-3.5' : 'h-4 w-4'
                      }`} />
                    )}
                  </Link>
                  
                  {item.subItems && (
                    <div
                      className="absolute left-0 mt-1 w-52 max-h-[min(28rem,calc(100dvh-7rem))] overflow-y-auto overscroll-y-contain rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
                      role="menu"
                      aria-label={`${item.label} alt menü`}
                    >
                      <div className="py-1">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.label}
                            href={subItem.href}
                            role="menuitem"
                            className="block px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-100 hover:text-primary leading-tight"
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>
            
            {/* Arama ve Menü Butonları */}
            <div className="flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="icon"
                className="text-gray-700 hover:text-primary hover:bg-gray-100 transition-all duration-300 h-11 w-11 min-h-[44px] min-w-[44px]"
                aria-label="Ürün ara"
                title="Ürün ara"
                onClick={() => setSearchModalOpen(true)}
              >
                <Search className="h-5 w-5 transition-all duration-300" />
              </Button>
              
              <Button 
                variant="ghost" 
                size="icon"
                className="text-gray-700 hover:text-primary hover:bg-gray-100 transition-all duration-300 h-11 w-11 min-h-[44px] min-w-[44px]"
                aria-label="Favoriler"
                title="Favoriler"
                onClick={() => window.location.href = '/favoriler'}
              >
                <Tag className="h-5 w-5 transition-all duration-300" />
              </Button>
              
              {/* Mobil Menü Butonu */}
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-700 hover:text-primary hover:bg-gray-100 md:hidden transition-all duration-300 h-11 w-11 min-h-[44px] min-w-[44px]"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={mobileMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobil Menü */}
      <div 
        id="mobile-menu"
        className="fixed top-0 right-0 h-full w-4/5 max-w-xs bg-white z-40 pt-8 overflow-y-auto transition-transform transition-opacity duration-300 ease-in-out md:hidden shadow-lg rounded-r-2xl"
        style={{
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
          opacity: mobileMenuOpen ? '1' : '0',
          pointerEvents: mobileMenuOpen ? 'auto' : 'none'
        }}
      >
        <div className="px-4">
          {/* Sadece kapatma butonu sağ üstte */}
          <div className="flex justify-end mb-1 pb-1 sticky top-0 bg-white z-10">
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-700 hover:text-primary hover:bg-gray-100 h-10 w-10 rounded-full transition-colors duration-200"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Menüyü kapat"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <nav className="flex flex-col space-y-2">
            {navItems.map((item) => (
              <div key={item.label} className="rounded-md overflow-hidden">
                {item.subItems ? (
                  <>
                    <div 
                      className={`flex items-center justify-between px-4 py-3 text-base font-medium rounded-md cursor-pointer transition-colors duration-200 ${
                        pathname === item.href || pathname?.startsWith(item.href + '/') 
                          ? 'text-primary-darkest bg-red-50' 
                          : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                      }`}
                      onClick={() => setOpenSubMenu(openSubMenu === item.label ? null : item.label)}
                    >
                      <div className="flex items-center">
                        {item.icon && <item.icon className={`h-5 w-5 mr-3 transition-colors duration-200 ${
                          pathname === item.href || pathname?.startsWith(item.href + '/') 
                            ? 'text-primary-darkest' 
                            : 'text-gray-600'
                        }`} />}
                        {item.label}
                      </div>
                      <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${
                        pathname === item.href || pathname?.startsWith(item.href + '/') 
                          ? 'text-primary-darkest' 
                          : 'text-gray-600'
                        } ${openSubMenu === item.label ? 'rotate-180' : ''}`} />
                    </div>
                    
                    {item.subItems && (
                      <div className={`ml-6 mt-1 space-y-1 overflow-hidden transition-all duration-300 origin-top
                        ${openSubMenu === item.label ? 'scale-y-100 opacity-100 max-h-[1000px] h-auto' : 'scale-y-0 opacity-0 max-h-0 h-0'}
                      `}>
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.label}
                            href={subItem.href}
                            className={`block px-4 py-2 text-sm rounded-md transition-colors duration-200 ${
                              pathname === subItem.href 
                                ? 'text-primary-darkest bg-red-50 font-medium' 
                                : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                            }`}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <div className="flex items-center">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mr-2"></span>
                              {subItem.label}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link 
                    href={item.href}
                    className={`flex items-center px-4 py-3 text-base font-medium rounded-md ${
                      pathname === item.href 
                        ? 'text-primary-darkest bg-red-50' 
                        : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.icon && <item.icon className={`h-5 w-5 mr-3 ${
                      pathname === item.href ? 'text-primary-darkest' : 'text-gray-600'
                    }`} />}
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>
          
          <div className="mt-8 border-t border-gray-100 pt-6 space-y-4">
            <div className="flex flex-wrap gap-4 pb-4">
              <Link
                href="/katalog"
                className="flex min-h-[44px] items-center py-2 text-gray-700 transition-transform duration-200 hover:text-primary active:scale-[0.98]"
                onClick={() => setMobileMenuOpen(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="mr-1 h-4 w-4 shrink-0 text-primary" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                </svg>
                Katalog
              </Link>
              <Link
                href="/kartela"
                className="flex min-h-[44px] items-center py-2 text-gray-700 transition-transform duration-200 hover:text-primary active:scale-[0.98]"
                onClick={() => setMobileMenuOpen(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z" clipRule="evenodd" />
                </svg>
                Renk Kartelası
              </Link>
            </div>
            
            <div className="flex items-center">
              <Phone className="h-5 w-5 mr-3 text-primary" />
              <Link 
                href="tel:+905323457227" 
                className="text-gray-700 hover:text-primary transition"
                onClick={() => setMobileMenuOpen(false)}
              >
                +90 532 345 72 27
              </Link>
            </div>
            
            <div className="flex items-center">
              <MapPin className="h-5 w-5 mr-3 text-primary" />
              <span className="text-gray-700">İstanbul, Türkiye</span>
            </div>
            
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-3 text-primary" />
              <span className="text-gray-700">Pzt-Cmt: 09:00 - 18:00</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Dışarı tıklama ile kapatma için yarı saydam overlay */}
      <div 
        className="absolute inset-0 bg-black/20 -z-10" 
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
        style={{transition: 'none'}}
      />
      
      {/* Arama Modalı */}
      {isSearchModalOpen && (
        <SearchModal onClose={() => setSearchModalOpen(false)} />
      )}
    </header>
  );
};

export default Header; 